import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import bmsService, { BmsBooking, BmsCalendarResource } from "./bmsService";

interface BmsState {
  bookings: BmsBooking[];
  bookingsTotal: number;
  calendarResources: BmsCalendarResource[];
  isLoading: boolean;
  error: string | null;
}

const initialState: BmsState = {
  bookings: [],
  bookingsTotal: 0,
  calendarResources: [],
  isLoading: false,
  error: null,
};

export const fetchBmsBookings = createAsyncThunk(
  "bms/fetchBookings",
  async ({ chatbotId, params }: { chatbotId: number; params?: any }, thunkAPI) => {
    try {
      return await bmsService.listBookings(chatbotId, params);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || error.message || "Failed to fetch bookings");
    }
  }
);

export const fetchBmsCalendar = createAsyncThunk(
  "bms/fetchCalendar",
  async ({ chatbotId, params }: { chatbotId: number; params: { start_at: string; end_at: string } }, thunkAPI) => {
    try {
      return await bmsService.getCalendar(chatbotId, params);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || error.message || "Failed to fetch calendar");
    }
  }
);

const bmsSlice = createSlice({
  name: "bms",
  initialState,
  reducers: {
    clearBmsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBmsBookings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBmsBookings.fulfilled, (state, action: PayloadAction<{ data: BmsBooking[]; total: number }>) => {
        state.isLoading = false;
        state.bookings = action.payload.data || [];
        state.bookingsTotal = action.payload.total ?? 0;
      })
      .addCase(fetchBmsBookings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchBmsCalendar.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBmsCalendar.fulfilled, (state, action: PayloadAction<{ resources: BmsCalendarResource[] }>) => {
        state.isLoading = false;
        state.calendarResources = action.payload.resources || [];
      })
      .addCase(fetchBmsCalendar.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearBmsError } = bmsSlice.actions;
export default bmsSlice.reducer;

